import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import ModalDialogMUI6 from 'src/components/ModalDialogMUI6';
import axios from 'src/utils/axios';

const REPORT_NOT_DELIVERED_VIDEO_URL = 'https://player.vimeo.com/video/1177930852';

const STATUS_OPTIONS = [
  { value: 'not_delivered', label: 'Not Delivered' },
  { value: 'delivered_spam', label: 'Went to Spam' },
  { value: 'delivered_delayed', label: 'Delivered Late' },
  { value: 'delivered', label: 'Mark as Delivered' },
];

function ReportNotDeliveredModal({
  open,
  onClose,
  message = null,
  contactCarrier = null,
  isUpdate = false,
  existingReport = null,
  onSuccess = () => {},
}) {
  const [reportedStatus, setReportedStatus] = useState('not_delivered');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    if (open) {
      if (isUpdate && existingReport) {
        setReportedStatus(existingReport.reportedStatus || 'not_delivered');
        setNotes(existingReport.notes || '');
      } else {
        setReportedStatus('not_delivered');
        setNotes('');
      }
      setSubmitError(null);
    }
  }, [open, isUpdate, existingReport]);

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);

    try {
      if (isUpdate && existingReport) {
        await axios.put(`/api/delivery-reports/${existingReport.id}`, {
          reportedStatus,
          notes,
        });
      } else {
        await axios.post('/api/delivery-reports', {
          messageId: message?.id,
          reportedStatus,
          notes,
        });
      }
      onSuccess(isUpdate ? 'Report updated' : 'Report submitted');
      onClose();
    } catch (err) {
      setSubmitError(
        err?.response?.data?.error || 'Failed to submit report. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const bodyText = message?.body || '';
  const preview = bodyText.length > 200
    ? `${bodyText.slice(0, 200)}...`
    : bodyText;
  const messageScore = message?.score || null;

  const content = (
    <Box sx={{ minWidth: 300, maxWidth: 480 }}>
      {/* Message preview */}
      <Tooltip
        title={(
          <span style={{ whiteSpace: 'pre-line' }}>
            {bodyText}
          </span>
        )}
        placement="top"
        arrow
      >
        <Typography
          variant="body2"
          sx={{
            color: '#546e7a',
            fontSize: '13px',
            lineHeight: 1.4,
            mb: 2,
            p: 1.5,
            borderRadius: 1,
            backgroundColor: '#f5f5f5',
            border: '1px solid #e0e0e0',
            cursor: 'default',
            whiteSpace: 'pre-line',
            wordBreak: 'break-word',
            maxHeight: 80,
            overflow: 'hidden',
          }}
        >
          {preview || '(no message)'}
        </Typography>
      </Tooltip>

      {/* Recipient carrier */}
      {contactCarrier && (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 2,
        }}
        >
          <Typography variant="body2" sx={{ color: '#37474f' }}>
            Sent to
          </Typography>
          <Chip
            label={contactCarrier}
            size="small"
            sx={{
              fontSize: '11px',
              height: 22,
              backgroundColor: '#e3f2fd',
              color: '#1565c0',
            }}
          />
        </Box>
      )}

      {/* Score nudge */}
      {messageScore != null && messageScore < 80 && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: '#e65100',
            mb: 2,
          }}
        >
          {`This message scored ${messageScore}/100 \u2014 improving it may increase future delivery rates.`}
        </Typography>
      )}

      {/* Status radio group */}
      <FormControl sx={{ mb: 2, width: '100%' }}>
        <FormLabel
          component="div"
          sx={{ color: 'text.primary', fontWeight: 600, mb: 0.5 }}
        >
          What happened?
        </FormLabel>
        <RadioGroup
          value={reportedStatus}
          onChange={(e) => setReportedStatus(e.target.value)}
        >
          {STATUS_OPTIONS.map((option) => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              control={<Radio size="small" />}
              label={option.label}
            />
          ))}
        </RadioGroup>
      </FormControl>

      {/* Notes */}
      <TextField
        label="Notes (optional)"
        multiline
        rows={3}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        fullWidth
        size="small"
        sx={{ mb: 2 }}
      />

      {/* Error */}
      {submitError && (
        <Typography
          variant="body2"
          sx={{ color: '#c62828', mb: 1 }}
        >
          {submitError}
        </Typography>
      )}
    </Box>
  );

  return (
    <ModalDialogMUI6
      isVisible={open}
      onClose={onClose}
      title={(
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
        }}
        >
          <ReportProblemOutlinedIcon sx={{ color: '#e65100' }} />
          {isUpdate ? 'Update Report' : 'Report Not Delivered'}
        </Box>
      )}
      content={content}
      closeText="Cancel"
      okText={submitting ? 'Submitting...' : (isUpdate ? 'Update Report' : 'Confirm Report')}
      okDisabled={submitting}
      okIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
      onOk={handleSubmit}
      buttonsListOrder={['close', 'ok']}
      modalSize="sm"
      showVideo
      videoURL={REPORT_NOT_DELIVERED_VIDEO_URL}
      videoTooltip="How to Report Not Delivered"
    />
  );
}

ReportNotDeliveredModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  message: PropTypes.shape({
    id: PropTypes.number,
    body: PropTypes.string,
    score: PropTypes.number,
  }),
  contactCarrier: PropTypes.string,
  isUpdate: PropTypes.bool,
  existingReport: PropTypes.shape({
    id: PropTypes.number,
    reportedStatus: PropTypes.string,
    notes: PropTypes.string,
  }),
  onSuccess: PropTypes.func,
};

export default ReportNotDeliveredModal;
